import React, { useState, useEffect, useRef } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  IconButton,
  Typography,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { keyframes } from '@mui/system';
import { useNavigate, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownRounded from '@mui/icons-material/KeyboardArrowDownRounded';
import SlideshowRounded from '@mui/icons-material/SlideshowRounded';
import GridOnRounded from '@mui/icons-material/GridOnRounded';
import DescriptionRounded from '@mui/icons-material/DescriptionRounded';
import AccountBalanceRounded from '@mui/icons-material/AccountBalanceRounded';
import TrendingUpRounded from '@mui/icons-material/TrendingUpRounded';
import GroupsRounded from '@mui/icons-material/GroupsRounded';
import WorkOutlineRounded from '@mui/icons-material/WorkOutlineRounded';
import ArticleRounded from '@mui/icons-material/ArticleRounded';
import SupportAgentRounded from '@mui/icons-material/SupportAgentRounded';
import CompareArrowsRounded from '@mui/icons-material/CompareArrowsRounded';
import SkipLink from './SkipLink';
import { brandConfig } from '../design/theme';

// Single source of truth for the marketing header. This is the exact
// landing-page AppBar (transparent -> blur-on-scroll, fixed, full-width
// gutters, rust logo lockup, centered nav, Login + dark "See it on your own deck",
// mobile hamburger menu, and the logged-in user menu) extracted so the same
// header renders on the landing page and every secondary page (/pricing,
// /security, /demo) with no drift.
//
// Nav links smooth-scroll to in-page sections on the home route, and on any
// other route navigate to the home page with the matching hash so the user
// still lands on that section.
//
// Props:
//   onLogin  optional. The landing page passes its sign-in dialog opener so
//            "Login" opens the popup in place. On secondary pages it is
//            omitted, and "Login" navigates to the home page instead.
// Staggered entrance for mega-menu items (re-runs on each open via React key).
const megaItemAnim = keyframes`
  from { opacity: 0; transform: translateY(9px); }
  to { opacity: 1; transform: translateY(0); }
`;

// The console lives on its own subdomain; the marketing header only points at
// it. Module scope so the nav list and the mobile menu share one URL.
const CONSOLE_HREF = 'https://console.re-stack.com';

// Top-level nav. Product, Solutions, and Company are full-width mega-menus
// (they carry `intro` + `children`); Security is a single destination. A child
// carries `to` (route) or `id` (in-page section on the home route, /#id
// elsewhere), a `desc` line, and an `icon`.
const navItems = [
  {
    label: 'Product',
    intro: {
      eyebrow: 'Product',
      title: 'AI for Private Equity teams',
      desc: "The deck, model, and memo your Private Equity team rebuilds every cycle, on your firm's template in your live Office files, with the chart grammar and citations a generic copilot skips.",
    },
    children: [
      { label: 'For PowerPoint', desc: 'Build the deck, every figure cited', to: '/product', icon: <SlideshowRounded /> },
      { label: 'For Excel', desc: 'Build and audit the model', to: '/excel', icon: <GridOnRounded /> },
      { label: 'For Word', desc: 'Draft and redline the memo', to: '/word', icon: <DescriptionRounded /> },
      { label: 'vs Claude', desc: 'Where the generalist stops', to: '/vs/claude', icon: <CompareArrowsRounded /> },
      { label: 'vs Copilot', desc: 'The honest comparison', to: '/vs/copilot', icon: <CompareArrowsRounded /> },
    ],
  },
  {
    label: 'Solutions',
    intro: {
      eyebrow: 'Solutions',
      title: 'Built for funds and case teams',
      desc: 'What private equity, growth equity, and consulting teams actually ship with Restack.',
    },
    children: [
      { label: 'Private Equity', desc: 'Board packs, IC decks, value-creation plans', to: '/solutions/private-equity', icon: <AccountBalanceRounded /> },
      { label: 'Growth Equity', desc: 'Market maps, sourcing, theses', to: '/solutions/growth-equity', icon: <TrendingUpRounded /> },
      { label: 'Consulting', desc: 'SteerCo decks, market sizing, client reporting', to: '/solutions/consulting', icon: <GroupsRounded /> },
    ],
  },
  { label: 'Security', to: '/security' },
  {
    label: 'Company',
    intro: {
      eyebrow: 'Company',
      title: 'About Restack',
      desc: "Who we are, what's new, and how to reach the team.",
    },
    children: [
      { label: 'Careers', desc: 'Join the team', to: '/company#careers', icon: <WorkOutlineRounded /> },
      { label: 'News', desc: 'Latest from Restack', to: '/company#news', icon: <ArticleRounded /> },
      { label: 'Support', desc: 'Get help, 24h reply', to: '/company#support', icon: <SupportAgentRounded /> },
    ],
  },
  // The console sits in the nav row now, after the product story. AI
  // Transformation has moved out to the right, paired with the primary CTA, so
  // the practice reads as its own prominent destination rather than one more
  // grey nav word.
  { label: 'Console', href: CONSOLE_HREF, external: true },
];

// `pageMeasure` lets a page hand the header its own content measure so the
// logo and the right-hand CTA sit on the same two vertical edges as the copy
// underneath. The site has no single measure (pages run 1000 to 1320), so the
// default stays what it always was and only a page that asks gets moved.
export const SiteHeader = ({ onLogin, hideAuthActions = false, offsetTop = 0, pageMeasure = null }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const isHome = location.pathname === '/';
  const dark = theme.palette.mode === 'dark';

  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuAnchorEl, setMobileMenuAnchorEl] = useState(null);
  const mobileMenuOpen = Boolean(mobileMenuAnchorEl);
  // Which desktop megamenu is open (by label), or null. Hover-driven. A
  // separate `menuContent` lags behind so the panel keeps rendering its last
  // content through the fade-out instead of blanking.
  const [openDropdown, setOpenDropdown] = useState(null);
  const [menuContent, setMenuContent] = useState(null);
  const closeTimer = useRef(null);
  const clearContentTimer = useRef(null);
  const openMenu = (label) => {
    clearTimeout(closeTimer.current);
    clearTimeout(clearContentTimer.current);
    setMenuContent(label);
    setOpenDropdown(label);
  };
  const scheduleClose = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => {
      setOpenDropdown(null);
      clearTimeout(clearContentTimer.current);
      clearContentTimer.current = setTimeout(() => setMenuContent(null), 260);
    }, 110);
  };
  const closeMenuNow = () => {
    clearTimeout(closeTimer.current);
    clearTimeout(clearContentTimer.current);
    setOpenDropdown(null);
    setMenuContent(null);
  };
  useEffect(() => () => {
    clearTimeout(closeTimer.current);
    clearTimeout(clearContentTimer.current);
  }, []);

  // Shared style for the top-level nav links.
  const navLinkSx = {
    fontFamily: theme.typography.fontFamily,
    fontSize: '0.8rem',
    fontWeight: 400,
    letterSpacing: '-0.005em',
    color: theme.palette.text.secondary,
    textDecoration: 'none',
    cursor: 'pointer',
    transition: 'color 0.2s ease',
    '&:hover': { color: theme.palette.text.primary },
  };

  // The AI transformation practice is a different kind of destination from the
  // product links beside it, so it carries the brand rust rather than the muted
  // nav grey. It stops at a colour change: the outlined pill on the right
  // already points at /ai-transformation, and a second accented control for it
  // in the same bar competes with the first instead of adding to it.
  const accentNavLinkSx = {
    ...navLinkSx,
    fontWeight: 500,
    color: '#B83B1D',
    '&:hover': { color: '#A63419' },
  };

  // The item for the page you are already on. A rust underline rather than a
  // colour change, because Advisory is already rust and would have nothing
  // left to say otherwise.
  const activeNavLinkSx = {
    color: '#B83B1D',
    fontWeight: 500,
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: -6,
      height: '2px',
      borderRadius: '2px',
      backgroundColor: '#B83B1D',
    },
    '&:hover': { color: '#A63419' },
  };

  // The marketing pages scroll inside a container (LandingContainer), not the
  // window, so a plain window scroll listener never fires. A capture-phase
  // listener catches scroll events from ANY scrolling element (scroll events
  // don't bubble, but they do travel through the capture phase), and we read
  // the position from the event target, falling back to the window.
  useEffect(() => {
    const readTop = (e) => {
      const t = e && e.target;
      if (t && t !== document && t !== window && typeof t.scrollTop === 'number') {
        return t.scrollTop;
      }
      return window.scrollY || document.documentElement.scrollTop || 0;
    };
    const onScroll = (e) => {
      // Ignore scrolls inside the floating FAQ chat widget so it doesn't
      // toggle the header chrome.
      const t = e && e.target;
      if (t && t.closest && t.closest('.faq-chat-messages')) return;
      setScrolled(readTop(e) > 16);
    };
    setScrolled((window.scrollY || document.documentElement.scrollTop || 0) > 16);
    window.addEventListener('scroll', onScroll, { passive: true, capture: true });
    return () => window.removeEventListener('scroll', onScroll, { capture: true });
  }, []);

  const closeMobileMenu = () => setMobileMenuAnchorEl(null);

  // Smooth-scroll on home; otherwise navigate to the home page at the hash so
  // the browser lands on the section.
  const goToSection = (id) => {
    if (isHome) {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.location.assign(`/#${id}`);
    }
  };

  // Unified nav action for both top-level items and dropdown children: a `to`
  // routes, an `id` scrolls to (or navigates to) the home section.
  const handleNavClick = (item) => {
    if (item.to) navigate(item.to);
    else if (item.id) goToSection(item.id);
  };

  return (
    <>
      {/* First tab stop on every marketing page. */}
      <SkipLink />
      <AppBar
        position="fixed"
        color="transparent"
        elevation={0}
        sx={{
          top: offsetTop,
          // The styling lives on the inner Toolbar "pill" now (so it can shrink
          // toward center on scroll, Voiceflow-style); the bar itself is just a
          // transparent full-width positioning shell.
          backgroundColor: 'transparent',
          backdropFilter: 'none',
          WebkitBackdropFilter: 'none',
          borderBottom: '1px solid transparent',
          boxShadow: 'none',
          transition: 'top 0.3s ease',
          '&::before': { display: 'none' },
          '&::after': { display: 'none' },
          zIndex: 1100,
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            // At the top: full-width bar (maxWidth 1280, spread). On scroll: it
            // compresses into a centered floating pill, max-width shrinks so the
            // logo and the right-side buttons slide inward toward the centered
            // nav, the corners round, and a blurred panel + shadow lifts it off
            // the page.
            position: 'relative',
            width: '100%',
            mx: 'auto',
            // 1140 (was 1100, 980, 880): the nav is absolutely centered, so it
            // grows symmetrically and its right edge runs at Console. The
            // wider "See it on your own deck" CTA and the sixth nav item both
            // eat into that gap; at 980 "Advisory" sat on top of "Console"
            // once the pill compressed, and the longer "AI Transformation"
            // pill left only 14px at 1100 on a 1200px viewport.
            maxWidth: { xs: '100%', md: scrolled ? 1140 : (pageMeasure ? pageMeasure.maxWidth : 1280) },
            mt: { xs: 0, md: scrolled ? 1.25 : 0 },
            px: { xs: 1.5, md: scrolled ? 2.5 : (pageMeasure ? pageMeasure.px : 3) },
            py: { xs: 0.75, md: scrolled ? 0.65 : 1 },
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderRadius: { xs: 0, md: scrolled ? '999px' : '0px' },
            backgroundColor: scrolled
              ? (theme.palette.mode === 'dark' ? 'rgba(28, 28, 28, 0.78)' : 'rgba(250, 249, 247, 0.82)')
              : 'transparent',
            backdropFilter: scrolled ? 'blur(18px) saturate(1.2)' : 'none',
            WebkitBackdropFilter: scrolled ? 'blur(18px) saturate(1.2)' : 'none',
            border: scrolled
              ? `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.09)' : 'rgba(46,20,10,0.09)'}`
              : '1px solid transparent',
            boxShadow: scrolled
              ? (theme.palette.mode === 'dark' ? '0 8px 30px rgba(0,0,0,0.35)' : '0 10px 34px rgba(46,20,10,0.10)')
              : 'none',
            transition: 'max-width 0.45s cubic-bezier(0.16,1,0.3,1), margin-top 0.45s cubic-bezier(0.16,1,0.3,1), padding 0.4s ease, border-radius 0.4s ease, background-color 0.35s ease, box-shadow 0.35s ease, border-color 0.35s ease',
          }}
        >
          {/* Left side - Logo + nav links (desktop) */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, md: 3.5 }, minWidth: 0 }}>
            <Box
              component="a"
              href="/"
              sx={{ display: 'flex', alignItems: 'center', gap: 1, textDecoration: 'none' }}
            >
              <brandConfig.icon sx={{ fontSize: 18, color: '#B83B1D' }} />
              <Typography
                variant="h6"
                // Element, not level: the wordmark is a logo, not a section
                // heading. Left as a real <h6> it opened every marketing page
                // with an h6 before the h1, breaking heading order site-wide.
                // Every type property the variant sets is overridden below, so
                // this changes the element only.
                component="span"
                sx={{
                  fontWeight: 600,
                  fontSize: '15px',
                  color: '#B83B1D',
                  letterSpacing: '-0.015em',
                  fontFamily: '"Inter", sans-serif',
                  fontFeatureSettings: '"cv11", "ss01"',
                  lineHeight: 1,
                }}
              >
                {brandConfig.name}
              </Typography>
            </Box>

            {/* Centered nav (desktop) */}
            <Box
              sx={{
                // The centered nav only appears once there is room for it
                // beside the logo on the left and Console + the CTA on the
                // right. Below that the hamburger carries navigation, which is
                // what a 6-item nav needs on a small laptop.
                display: { xs: 'none', lg: 'flex' },
                alignItems: 'center',
                gap: { lg: 2.5, xl: 3.5 },
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
              }}
            >
              {navItems.map((item) => {
                if (!item.children) {
                  // An external item (Console) is a plain new-tab anchor; an
                  // internal one routes or scrolls to a home-page section.
                  if (item.external) {
                    return (
                      <Typography
                        key={item.label}
                        component="a"
                        href={item.href}
                        target="_blank"
                        rel="noopener"
                        sx={navLinkSx}
                      >
                        {item.label}
                      </Typography>
                    );
                  }
                  return (
                    <Typography
                      key={item.label}
                      component="a"
                      href={item.to ? item.to : (isHome ? `#${item.id}` : `/#${item.id}`)}
                      onClick={(e) => {
                        if (item.to) {
                          e.preventDefault();
                          navigate(item.to);
                        } else if (isHome) {
                          e.preventDefault();
                          goToSection(item.id);
                        }
                      }}
                      aria-current={location.pathname === item.to ? 'page' : undefined}
                      sx={{
                        ...(item.accent ? accentNavLinkSx : navLinkSx),
                        ...(location.pathname === item.to ? activeNavLinkSx : null),
                      }}
                    >
                      {item.label}
                    </Typography>
                  );
                }
                const open = openDropdown === item.label;
                return (
                  <Box
                    key={item.label}
                    onMouseEnter={() => openMenu(item.label)}
                    onMouseLeave={scheduleClose}
                    sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}
                  >
                    <Box
                      component="span"
                      sx={{
                        ...navLinkSx,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.25,
                        color: open ? theme.palette.text.primary : theme.palette.text.secondary,
                      }}
                    >
                      {item.label}
                      <KeyboardArrowDownRounded
                        sx={{
                          fontSize: 15,
                          transition: 'transform 0.2s ease',
                          transform: open ? 'rotate(180deg)' : 'none',
                          opacity: 0.7,
                        }}
                      />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Right side - Auth actions + CTA */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {!hideAuthActions && (<>
            {/* AI Transformation, pulled out of the nav row and paired with
                the primary CTA so the practice reads as its own prominent
                destination. An outlined rust pill sits a clear notch above the
                grey nav words without competing with the solid dark CTA beside
                it; on that page itself it fills in to mark the current
                section. */}
            {(() => {
              const onPractice = location.pathname === '/ai-transformation';
              return (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => navigate('/ai-transformation')}
                  aria-current={onPractice ? 'page' : undefined}
                  sx={{
                    borderRadius: '999px',
                    height: 34,
                    minHeight: 34,
                    px: 1.8,
                    py: 0,
                    fontFamily: theme.typography.fontFamily,
                    fontWeight: 500,
                    fontSize: '0.8rem',
                    letterSpacing: '-0.005em',
                    lineHeight: 1,
                    textTransform: 'none',
                    boxShadow: 'none',
                    color: '#B83B1D',
                    borderColor: alpha('#B83B1D', onPractice ? 0.5 : 0.32),
                    backgroundColor: onPractice ? alpha('#B83B1D', 0.08) : 'transparent',
                    display: { xs: 'none', sm: 'inline-flex' },
                    transition: 'background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease',
                    '&:hover': {
                      borderColor: alpha('#B83B1D', 0.6),
                      backgroundColor: alpha('#B83B1D', 0.06),
                      color: '#A63419',
                    },
                  }}
                >
                  AI Transformation
                </Button>
              );
            })()}

            {/* The persistent product CTA, pointing at the demo. AI
                Transformation has its own control to the left of this one, so
                the two asks are split cleanly: the outlined pill routes to the
                practice,
                and this solid CTA carries the product story to /demo ("see it on
                your own deck"). */}
            <Button
              variant="contained"
              size="small"
              onClick={() => navigate('/demo')}
              sx={{
                // Fully rounded to match the floating header pill (999px).
                borderRadius: '999px',
                height: 34,
                minHeight: 34,
                px: 1.8,
                py: 0,
                fontFamily: theme.typography.fontFamily,
                fontWeight: 400,
                fontSize: '0.8rem',
                letterSpacing: '-0.005em',
                lineHeight: 1,
                textTransform: 'none',
                boxShadow: 'none',
                border: 'none',
                backgroundColor: theme.palette.mode === 'dark' ? '#ffffff' : '#2E140A',
                color: theme.palette.mode === 'dark' ? '#1a1a1a' : '#ffffff',
                display: { xs: 'none', sm: 'inline-flex' },
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark' ? '#f0f0f0' : '#1A0A04',
                  boxShadow: theme.palette.mode === 'dark'
                    ? '0 4px 12px rgba(255, 255, 255, 0.25)'
                    : '0 4px 12px rgba(0, 0, 0, 0.25)',
                },
              }}
            >
              See it on your own deck
            </Button>
            </>)}

            {/* Hamburger (mobile only) */}
            <IconButton
              onClick={(e) => setMobileMenuAnchorEl(e.currentTarget)}
              sx={{ display: { xs: 'flex', lg: 'none' }, color: theme.palette.text.primary, width: 44, height: 44 }}
              aria-label="Open navigation menu"
              aria-haspopup="true"
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
            </IconButton>
          </Box>

          {/* Desktop full-width mega-menu. One panel spanning the pill width,
              anchored just below it; content swaps to the hovered item. The
              top padding is an invisible hover bridge across the gap. */}
          {(() => {
            const active = navItems.find((i) => i.label === menuContent && i.children);
            const isOpen = Boolean(openDropdown);
            const divider = dark ? 'rgba(255,255,255,0.07)' : 'rgba(46,20,10,0.07)';
            return (
              <Box
                onMouseEnter={() => { clearTimeout(closeTimer.current); clearTimeout(clearContentTimer.current); }}
                onMouseLeave={scheduleClose}
                sx={{
                  // Opened only by the centered nav, so it follows the same gate.
                  display: { xs: 'none', lg: 'block' },
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  pt: 1.25,
                  opacity: isOpen ? 1 : 0,
                  visibility: isOpen ? 'visible' : 'hidden',
                  transform: isOpen ? 'translateY(0)' : 'translateY(-8px)',
                  transition: 'opacity 0.22s ease, transform 0.3s cubic-bezier(0.16,1,0.3,1), visibility 0.22s',
                  pointerEvents: isOpen ? 'auto' : 'none',
                  zIndex: 1200,
                }}
              >
                <Box
                  sx={{
                    borderRadius: '20px',
                    backgroundColor: dark ? 'rgba(26,26,26,0.98)' : 'rgba(255,255,255,0.98)',
                    backdropFilter: 'blur(22px) saturate(1.3)',
                    WebkitBackdropFilter: 'blur(22px) saturate(1.3)',
                    border: `1px solid ${dark ? 'rgba(255,255,255,0.09)' : 'rgba(46,20,10,0.09)'}`,
                    boxShadow: dark ? '0 24px 60px rgba(0,0,0,0.5)' : '0 24px 60px rgba(46,20,10,0.16)',
                    p: 3,
                  }}
                >
                  {active && (
                    <Box
                      key={menuContent}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '0.82fr 1.55fr',
                        gap: 4,
                        alignItems: 'start',
                      }}
                    >
                      {/* Left intro column */}
                      <Box sx={{ pr: 2, borderRight: `1px solid ${divider}` }}>
                        <Typography sx={{ fontFamily: theme.typography.fontFamily, fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#B83B1D', mb: 1 }}>
                          {active.intro.eyebrow}
                        </Typography>
                        <Typography sx={{ fontFamily: theme.typography.fontFamily, fontSize: '1.15rem', fontWeight: 600, letterSpacing: '-0.02em', color: theme.palette.text.primary, lineHeight: 1.25, mb: 1 }}>
                          {active.intro.title}
                        </Typography>
                        <Typography sx={{ fontFamily: theme.typography.fontFamily, fontSize: '0.82rem', lineHeight: 1.55, color: theme.palette.text.secondary }}>
                          {active.intro.desc}
                        </Typography>
                      </Box>

                      {/* Right items grid */}
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5 }}>
                        {active.children.map((child, ci) => (
                          <Box
                            key={child.label}
                            onClick={() => { closeMenuNow(); handleNavClick(child); }}
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 1.5,
                              p: 1.25,
                              borderRadius: '12px',
                              cursor: 'pointer',
                              opacity: 0,
                              animation: `${megaItemAnim} 0.4s cubic-bezier(0.16,1,0.3,1) forwards`,
                              animationDelay: `${0.04 * ci + 0.03}s`,
                              transition: 'background-color 0.15s ease',
                              '@media (prefers-reduced-motion: reduce)': { animation: 'none', opacity: 1 },
                              '&:hover': { backgroundColor: alpha(theme.palette.text.primary, 0.05) },
                              '&:hover .mega-ico': { backgroundColor: alpha('#B83B1D', 0.16), transform: 'scale(1.06)' },
                            }}
                          >
                            <Box
                              className="mega-ico"
                              sx={{
                                flexShrink: 0,
                                width: 38,
                                height: 38,
                                borderRadius: '12px',
                                backgroundColor: alpha('#B83B1D', 0.09),
                                color: '#B83B1D',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'background-color 0.2s ease, transform 0.2s cubic-bezier(0.16,1,0.3,1)',
                                '& svg': { fontSize: 20 },
                              }}
                            >
                              {child.icon}
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <Typography sx={{ fontFamily: theme.typography.fontFamily, fontSize: '0.86rem', fontWeight: 600, color: theme.palette.text.primary, lineHeight: 1.3 }}>
                                  {child.label}
                                </Typography>
                                {child.badge && (
                                  <Box
                                    component="span"
                                    sx={{
                                      flexShrink: 0,
                                      px: 0.7,
                                      py: '1px',
                                      borderRadius: '999px',
                                      backgroundColor: alpha('#B83B1D', 0.1),
                                      color: '#B83B1D',
                                      fontFamily: theme.typography.fontFamily,
                                      fontSize: '0.6rem',
                                      fontWeight: 700,
                                      letterSpacing: '0.04em',
                                      textTransform: 'uppercase',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {child.badge}
                                  </Box>
                                )}
                              </Box>
                              {child.desc && (
                                <Typography sx={{ fontFamily: theme.typography.fontFamily, fontSize: '0.74rem', color: theme.palette.text.secondary, lineHeight: 1.4, mt: 0.25 }}>
                                  {child.desc}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })()}
        </Toolbar>
      </AppBar>

      {/* Mobile nav dropdown - anchored to the hamburger. */}
      <Menu
        anchorEl={mobileMenuAnchorEl}
        open={mobileMenuOpen}
        onClose={closeMobileMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        keepMounted={false}
        sx={{ display: { xs: 'block', md: 'none' } }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              mt: 1,
              minWidth: 240,
              borderRadius: 2.5,
              border: `1px solid ${alpha(theme.palette.text.primary, 0.1)}`,
              boxShadow: theme.palette.mode === 'dark'
                ? '0 12px 32px rgba(0, 0, 0, 0.45)'
                : '0 12px 32px rgba(46, 20, 10, 0.12)',
              backgroundColor: theme.palette.background.paper,
              overflow: 'hidden',
            },
          },
        }}
      >
        {navItems.flatMap((item) => {
          if (!item.children) {
            const linkProps = item.external
              ? { component: 'a', href: item.href, target: '_blank', rel: 'noopener', onClick: closeMobileMenu }
              : { onClick: () => { closeMobileMenu(); handleNavClick(item); } };
            return [(
              <MenuItem
                key={item.label}
                {...linkProps}
                sx={{
                  py: 1.25,
                  px: 2.5,
                  fontFamily: theme.typography.fontFamily,
                  fontSize: '0.95rem',
                  fontWeight: 400,
                  color: theme.palette.text.primary,
                  '&:hover': { backgroundColor: alpha(theme.palette.text.primary, 0.05) },
                }}
              >
                {item.label}
              </MenuItem>
            )];
          }
          // Dropdown item: a non-clickable section label, then its children
          // as indented menu items. flatMap keeps these as direct Menu children.
          return [
            <Typography
              key={`${item.label}-header`}
              sx={{
                px: 2.5,
                pt: 1.5,
                pb: 0.5,
                fontFamily: theme.typography.fontFamily,
                fontSize: '0.68rem',
                fontWeight: 600,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: theme.palette.text.secondary,
              }}
            >
              {item.label}
            </Typography>,
            ...item.children.map((child) => (
              <MenuItem
                key={child.label}
                onClick={() => { closeMobileMenu(); handleNavClick(child); }}
                sx={{
                  py: 1,
                  px: 2.5,
                  fontFamily: theme.typography.fontFamily,
                  fontSize: '0.92rem',
                  fontWeight: 400,
                  color: theme.palette.text.primary,
                  gap: 1,
                  '&:hover': { backgroundColor: alpha(theme.palette.text.primary, 0.05) },
                }}
              >
                {child.label}
                {child.badge && (
                  <Box component="span" sx={{ px: 0.7, py: '1px', borderRadius: '999px', backgroundColor: alpha('#B83B1D', 0.1), color: '#B83B1D', fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                    {child.badge}
                  </Box>
                )}
              </MenuItem>
            )),
          ];
        })}

        {!hideAuthActions && (<>
        <Divider sx={{ my: 0.5, borderColor: alpha(theme.palette.text.primary, 0.08) }} />

        {/* AI Transformation pairs with the CTA here too, as the prominent outlined
            option above the solid primary. Console has moved into the nav list
            above, so it is no longer repeated down here. */}
        <Box sx={{ px: 1.5, py: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => { closeMobileMenu(); navigate('/ai-transformation'); }}
            sx={{
              borderRadius: '999px',
              py: 1.05,
              fontFamily: theme.typography.fontFamily,
              fontWeight: 500,
              fontSize: '0.9rem',
              textTransform: 'none',
              boxShadow: 'none',
              color: '#B83B1D',
              borderColor: alpha('#B83B1D', 0.38),
              backgroundColor: location.pathname === '/ai-transformation' ? alpha('#B83B1D', 0.08) : 'transparent',
              '&:hover': { borderColor: alpha('#B83B1D', 0.6), backgroundColor: alpha('#B83B1D', 0.06) },
            }}
          >
            AI Transformation
          </Button>
          <Button
            variant="contained"
            fullWidth
            onClick={() => { closeMobileMenu(); navigate('/demo'); }}
            sx={{
              borderRadius: '999px',
              py: 1.1,
              fontFamily: theme.typography.fontFamily,
              fontWeight: 600,
              fontSize: '0.9rem',
              textTransform: 'none',
              boxShadow: 'none',
              backgroundColor: theme.palette.mode === 'dark' ? '#ffffff' : '#2E140A',
              color: theme.palette.mode === 'dark' ? '#1a1a1a' : '#ffffff',
              '&:hover': { backgroundColor: theme.palette.mode === 'dark' ? '#f0f0f0' : '#1A0A04', boxShadow: 'none' },
            }}
          >
            See it on your own deck
          </Button>
        </Box>
        </>)}
      </Menu>
    </>
  );
};

export default SiteHeader;
